// Shared helpers, column definitions and small components used across the admin pages.
// Split out of the former single-file AdminPages.jsx; the code here is unchanged.
//
// txnGates()/gateReason() are the single place the admin client decides what a case allows —
// they mirror the server-side gates in backend/core/lifecycle.py. If you add a gate there,
// add it here in the same change (see backend/LIFECYCLE.md "The admin stage sequence").
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination, usePageSize, usePaged } from "@/components/Pagination";
import { useStickySearch } from "@/lib/stickySearch";
import { Input } from "@/components/ui/input";
import { StatusPill, pillLabel } from "@/components/StatusPill";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Search, MoreVertical, Copy, MessageCircle, Mail, ArrowUp, ArrowDown, ArrowUpDown, X, Pin } from "lucide-react";
import { api } from "@/lib/apiClient";
// `fmtDate` backs DateTimeCell below. This import was empty — the named binding had been dropped
// while the call site stayed — so every admin page rendering a DateTimeCell (Users, Orders,
// Payments, Backup) threw "fmtDate is not defined" and rendered nothing. Caught by the UI smoke
// suite (frontend/tests/e2e), which is precisely the class of failure the API tests cannot see.
import { fmtDate } from "@/lib/dates";
import { ALL_TIME, isoToday } from "@/components/DateRange";

// The recency window that used to live here — ALL_DAYS/DAY_WINDOWS/withinDays, offering
// 7/14/21/28 days and defaulting to 7 — is now @/components/DateRange, shared with the dashboard
// and the payments ledger and defaulted from Admin → Settings. It was the only one of the three
// ranged pages with its own option set, which is how the queue could show a week beside a
// dashboard showing a month with nothing on screen saying so.

// "All Open" — deliberately the same shape as the pending queues rather than a wide dump of every
// status dimension. Admin triages by *who owes the next move*, so the row is: which case, when,
// who it's for, where it is, and who it's waiting on. The six status dimensions are still all
// visible on the case detail page; spreading them across ten columns here made the list something
// to decode rather than scan.
export const ADMIN_TXN_COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "created", label: "Created" },
  { key: "modified", label: "Modified" },
  { key: "customer", label: "Customer" },
  { key: "status", label: "Status" },
  { key: "pendingAt", label: "Pending At" },
];

// mirrors TXN_GRID_COLS from CustomerDashboard.jsx — same minmax-floor trick so a long pill
// label can only push the row's own remaining columns right, never overlap its neighbor.
// Kept as narrow as each column's content allows (see renderRow — e.g. Reviewed By stacks its
// button+select vertically instead of side by side) so the whole table fits without horizontal
// scroll on a typical admin viewport instead of just being scrollable.
export const ADMIN_TXN_GRID_COLS = "grid-cols-[90px_100px_90px_minmax(160px,1fr)_minmax(120px,max-content)_minmax(130px,max-content)]";

// Compact date used across every admin transaction row — full-precision timestamps live on the
// detail page; the list view only needs enough to tell rows apart at a glance.
export const formatShortDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};

// "Modified" (updated_at) is what drives the default sort across every admin queue — see
// LIFECYCLE.md "Modified time": every change to a case (status/assignment/fulfillment, but also
// document edits and messages from any of the three roles) bumps updated_at, so sorting by it
// surfaces whichever case just had something happen on it, at the top.
export const formatRelativeTime = (iso) => {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatShortDate(iso);
};

export const ADMIN_TXN_SORT_ACCESSORS = {
  txn: (t) => t.id,
  created: (t) => new Date(t.created_at).getTime(),
  modified: (t) => new Date(t.updated_at || t.created_at).getTime(),
  customer: (t) => t.customer_name || "",
  service: (t) => t.service_type + (t.subtype || ""),
  status: (t) => t.status || "",
  reviewed: (t) => pillLabel(t.reviewed_by, "reviewed_by"),
  payment: (t) => pillLabel(t.payment_status, "payment_status"),
  softcopy: (t) => pillLabel(t.softcopy_status, "softcopy_status"),
  delivery: (t) => pillLabel(t.delivery_status, "delivery_status"),
  pending: (t) => new Date(t.admin_pending_since || t.created_at).getTime(),
  stage: (t) => t.admin_stage || "",
  assignedTo: (t) => t.reviewer_name || (t.reviewed_by === "admin" ? "you" : ""),
  messages: (t) => (t.customer_message_count || 0) + (t.reviewer_message_count || 0),
  reason: (t) => (t.action_items || []).filter(i => i.owner === "reviewer").map(i => i.label).join(","),
  closingNote: (t) => getClosingNote(t) || "",
  // Unpaid cases sort together at one end rather than scattering through the list.
  invoice: (t) => invoiceNo(t) || "",
  action: (t) => pillLabel(t.review_status, "review_status"),
};

// "Pending: Customer" tab is a stripped-down view (per admin request) — just enough to see
// what's sitting with the customer and for how long, none of the reviewer/fulfillment controls.
// Purely informational: this queue answers "what is the customer sitting on?", and admin has no
// move to make here — so no controls, just the facts, read-only. Status is always "In Draft" or
// "Payment Pending" (LIFECYCLE.md "Pending queues": customer-owned reasons only fire in those
// two states), which StatusPill renders straight from `status`.
export const PENDING_CUSTOMER_COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "created", label: "Created" },
  { key: "modified", label: "Modified" },
  { key: "customer", label: "Customer" },
  { key: "status", label: "Status" },
];
export const PENDING_CUSTOMER_GRID_COLS = "grid-cols-[90px_105px_100px_minmax(180px,1fr)_130px]";

// "Pending: Admin" / "Pending: Reviewer" clock — reads down to minutes, since those queues tend
// to get worked within the hour. (The customer queue shows Created + Modified instead: an
// elapsed-time column told admin nothing they couldn't read off those two.)
export const formatPendingDuration = (since) => {
  const totalMin = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 60000));
  if (totalMin < 60) return `${totalMin}m`;
  const totalHrs = Math.floor(totalMin / 60);
  if (totalHrs < 24) return `${totalHrs}h ${totalMin % 60}m`;
  const days = Math.floor(totalHrs / 24);
  return `${days}d ${totalHrs % 24}h`;
};

// India-only platform — assume a bare 10-digit number is a local mobile and prefix the country
// code `wa.me` needs. `mobile` is free-text with no stored format guarantee (see core/models.py),
// so anything that doesn't look like a plausible number just doesn't get a link (safer than a
// dead/wrong wa.me link) rather than guessing further.
export const toWhatsAppLink = (mobile) => {
  if (!mobile || mobile === "-") return null;
  const digits = mobile.replace(/\D/g, "");
  if (digits.length === 10) return `https://wa.me/91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `https://wa.me/${digits}`;
  return null;
};

// "Pending: Admin" tab — the full working queue: workflow stage, pending clock, assignment,
// fulfillment controls (soft copy / delivery) and the customer/reviewer message counts, each
// tucked behind a 3-dot menu so the row stays scannable. See AssignMenu below for the assign cell.
// Kept deliberately narrow so the whole queue fits one screen without horizontal scrolling:
// messages sit under the Txn ID (they identify a case, they aren't a workflow step), Status is
// read-only, workflow moves live in one "Action" menu, and both fulfillment channels collapse
// into two colour-coded icons under "Delivery".
export const PENDING_ADMIN_COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "modified", label: "Modified" },
  { key: "customer", label: "Customer" },
  { key: "stage", label: "Status" },
  { key: "pending", label: "Pending" },
  { key: "assignedTo", label: "Assigned To" },
  { key: "action", label: "Action" },
  { key: "delivery", label: "Delivery" },
];
export const PENDING_ADMIN_GRID_COLS = "grid-cols-[78px_74px_minmax(140px,1fr)_115px_64px_150px_130px_80px]";

// Red/green at a glance: green = done, red = nothing yet, amber = in flight. Hovering gives the
// real status, since collapsing the pills to icons trades text for width.
export const softcopyIconTone = (s) => (s === "available" ? "text-[#34D399]" : "text-[#F27373]");
export const deliveryIconTone = (s) => {
  if (s === "delivered") return "text-[#34D399]";
  if (s === "failed") return "text-[#F27373]";
  if (s === "not_started") return "text-[#F27373]";
  return "text-[#F5A524]"; // dispatched — in flight
};

// "Pending: Reviewer" tab — what's sitting with the reviewer: who it's assigned to (with a quick
// nudge + reassign), why it's still open (reusing the same reviewer-owned action-item badges as
// elsewhere), and how long. Deliberately no fulfillment controls — those are admin's job, not
// something this queue needs to expose.
export const PENDING_REVIEWER_COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "modified", label: "Modified" },
  { key: "customer", label: "Customer" },
  { key: "assignedTo", label: "Reviewer" },
  { key: "reason", label: "Reason" },
  { key: "pending", label: "Pending" },
  { key: "messages", label: "Messages" },
];
export const PENDING_REVIEWER_GRID_COLS = "grid-cols-[80px_90px_minmax(150px,1fr)_150px_150px_80px_90px]";

// "Completed" tab — read-mostly/archival: when it was created vs. closed, and — the whole point
// of the close-note requirement (see LIFECYCLE.md "Closing a case") — why admin closed it.
// Invoice appears **here and nowhere else**. A finished case is the one that gets asked about
// afterwards — resend the bill, which purchase was this on, an auditor tracing a document to a
// payment — and the invoice number is the only identifier the customer holds, since it is what
// their PDF prints. On the working queues it would be a column of blanks and noise; here it saves
// the trip to Payments that answering any of those questions used to require.
export const COMPLETED_COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "created", label: "Created" },
  { key: "modified", label: "Closed" },
  { key: "service", label: "Document" },
  { key: "payment", label: "Payment" },
  { key: "invoice", label: "Invoice" },
  { key: "closingNote", label: "Closing Note" },
];
// The room for Invoice comes out of Closing Note, which had the widest share and is the one column
// that reads fine truncated — it carries a `title` with the full text.
export const COMPLETED_GRID_COLS = "grid-cols-[80px_90px_90px_minmax(150px,1fr)_100px_95px_minmax(150px,1.1fr)]";

/**
 * The invoice number for a case, or null.
 *
 * Mirrors `routers/admin.py::admin_payments` — first 8 characters of the order id, uppercased —
 * because that is what `core.pdf._generate_bill_pdf` prints on the customer's copy, and a number
 * that does not match the paper is worse than no number. Keep the two in step.
 *
 * **Null is a real answer, not a gap.** A case closed without ever being paid — or refunded — has
 * no order, and rendering an empty cell there would read as data missing rather than as "there
 * was no invoice".
 *
 * **Not unique per row.** A cart checkout is one order covering several documents, so the same
 * number appears on each of them. That is the point (it groups a purchase), but it means this is
 * not a per-case identifier and must not be used as a key.
 */
// Matches core/delivery_partners.py's PLACEHOLDER exactly (case-insensitively, so a template
// typed as <tracking-id> still works) — the one place a delivery partner's URL template is turned
// into a real link. Shared by both delivery dialogs (the case page's and the queue's own) so a
// partner's tracking URL resolves identically wherever admin updates delivery from.
export const buildTrackingUrl = (template, trackingId) =>
  template ? template.replace(/<Tracking-Id>/i, trackingId || "") : "";

export const invoiceNo = (t) => ((t.order_id || "").slice(0, 8).toUpperCase() || null);

// Why a case was closed is *current state*, so it is a field on the transaction rather than
// something to find by walking its history — which the queues can no longer do anyway, now that
// the history has its own table and lists don't carry it (see AuditEvents in the backend).
export const getClosingNote = (t) => t.closing_note || null;

// Mirrors the server-side gates in core/lifecycle.py exactly — see LIFECYCLE.md "Gates".
// The backend is still the enforcement; this only stops the UI offering an action that would
// be rejected. Assignment alone is NOT enough to issue a document: the review must be complete,
// whoever is reviewing (including admin themself).
// Display text for the raw field names the backend reports in `customer_info_missing`
// (core.lifecycle.REQUIRED_CUSTOMER_FIELDS).
export const CUSTOMER_FIELD_LABELS = {
  mobile: "mobile number", address: "delivery address", email: "email address", name: "full name",
};

export const txnGates = (t) => {
  const assigned = t.reviewed_by !== "pending";
  const selfReview = t.reviewed_by === "admin";
  const reviewDone = t.review_status === "accepted";
  const live = t.status === "submitted";
  // Neither fulfillment channel opens until the customer has filled in their mobile and postal
  // address — only they can clear it, so this shows as its own stage rather than a refused click
  // (core.lifecycle.require_customer_info).
  const infoMissing = (t.customer_info_missing || []).length > 0;
  return {
    assigned,
    selfReview,
    reviewDone,
    infoMissing,
    // accepting an advocate's work needs them to have submitted it first
    canCompleteReview: live && assigned && !reviewDone && (selfReview || t.review_status === "submitted"),
    canIssue: live && assigned && reviewDone && !infoMissing,
    canDispatch: live && assigned && reviewDone && !infoMissing,
    // Completed = the work is done. Only offered once something has actually been delivered.
    canComplete: live && (t.softcopy_status === "available" || t.delivery_status === "delivered"),
    // Closed = seal the file. Available on a live or completed case; always needs a note.
    canClose: live || t.status === "completed",
  };
};

// Why an action is unavailable — shown on the disabled control so admin isn't left guessing
// which prerequisite is missing.
export const gateReason = (t, g, action) => {
  if (!g.assigned) return "Assign this case first";
  if (action !== "review" && !g.reviewDone) {
    return t.reviewed_by === "admin" ? "Complete your review first" : "Advocate's review not accepted yet";
  }
  // Names the missing fields, since admin's next step is to ring the customer and ask for them.
  if (action !== "review" && g.infoMissing) {
    const fields = (t.customer_info_missing || []).map(f => CUSTOMER_FIELD_LABELS[f] || f).join(" and ");
    return `Customer has no ${fields} on file — contact the customer`;
  }
  return null;
};

export const adminTxnMatchesSearch = (t, q) => {
  if (!q) return true;
  const hay = [
    t.id, t.customer_name, t.customer_email, t.customer_mobile, t.service_type, t.subtype,
    t.status, pillLabel(t.reviewed_by, "reviewed_by"),
    t.payment_status, pillLabel(t.payment_status, "payment_status"),
    t.softcopy_status, pillLabel(t.softcopy_status, "softcopy_status"),
    t.delivery_status, pillLabel(t.delivery_status, "delivery_status"),
    t.courier_name, t.tracking_id,
    // Both forms of the order reference: the invoice number as printed on the customer's bill,
    // and the full order id. A customer quoting the number on their PDF should land on the case
    // without anyone having to translate it first.
    t.order_id, invoiceNo(t),
    ...(t.action_items || []).map(i => i.label),
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q);
};

// Tabs group transactions by who owns the next move (derived server-side by
// core.lifecycle.compute_action_items — see backend/LIFECYCLE.md "Pending queues").
// A transaction can match more than one pending tab at once (e.g. admin owes a reply
// while the reviewer hasn't started), so these are membership filters, not a partition.
// The queues the page can show. `open`, `pending` and `closed` are the three the sidebar offers as
// destinations; the rest narrow Pending by who owes the next move and are reachable from the chips
// on that view. Kept as one list because it is also the allow-list for the `?tab=` parameter.
/**
 * The queue the Transactions page opens on, and the one a bare `/admin/transactions` means.
 *
 * **Pending**, not All Open. Pending is where the work is — cases waiting on somebody to move
 * them — and it is where an admin spends the day; All Open is the wider view, chosen deliberately
 * when you want to see everything rather than the thing you landed on. Shared with AdminLayout so
 * the sidebar highlight and the table can never disagree about which queue is showing.
 */
export const DEFAULT_TXN_QUEUE = "pending";

export const TXN_QUEUES = [
  { key: "open", label: "All Open" },
  { key: "pending", label: "Pending" },
  { key: "customer", label: "Pending: Customer", owner: "customer" },
  { key: "admin", label: "Pending: Admin", owner: "admin" },
  { key: "reviewer", label: "Pending: Reviewer", owner: "reviewer" },
  // Once a parcel is with the courier there is nothing for admin to *do* but await an outcome,
  // so it leaves the working queue and waits here. Keeps "Pending: Admin" to live work only.
  { key: "delivery", label: "Pending: Delivery" },
  { key: "closed", label: "Completed" },
];

// Shared across every admin transaction table. The visible id is truncated to 8 chars for
// scanning, but the copy button yields the FULL id — that's what admin needs when pasting into
// a search, an email or a support conversation, and retyping a uuid by eye is error-prone.
export const TxnIdCell = ({ t, onOpen, onTogglePin, children }) => {
  const copyId = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(t.id);
      toast.success("Transaction ID copied");
    } catch {
      toast.error("Could not copy — clipboard unavailable");
    }
  };
  return (
    <div className="pt-1.5 min-w-0">
      <div className="font-mono text-xs text-[#A0AABF] hover:text-white cursor-pointer truncate" onClick={onOpen}>
        {t.id.slice(0, 8)}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <button
          data-testid={`copy-txn-${t.id}`} onClick={copyId}
          title={`Copy full ID — ${t.id}`} aria-label="Copy transaction ID"
          className="text-[#6B7280] hover:text-[#CBA96C] transition-colors"
        ><Copy className="w-3 h-3"/></button>
        {onTogglePin && <PinButton pinned={t.pinned} onToggle={onTogglePin} testid={`pin-txn-${t.id}`}/>}
        {children}
      </div>
    </div>
  );
};

/**
 * Pins — rows an admin holds at the top of a list (Transactions queues, Payments & Orders).
 *
 * Stored per admin on the server (`admin_pins`, `PUT`/`DELETE /admin/pins/{kind}/{id}`) and
 * reported as `pinned` on each row of the list routes, so a pin follows the admin across browsers
 * and one admin's pins never rearrange another's queue. The ordering is done here, on top of
 * whatever sort the table has: `pinnedFirst` wraps a comparator so pinned rows lead and each group
 * keeps the chosen order within it.
 *
 * A pinned row also ignores the page's date window — pinning something is asking to keep it in
 * view, and a case that goes quiet for a week is exactly the one that would otherwise drop out.
 * Search and the queue it belongs to still apply.
 */
export const pinnedFirst = (cmp) => (a, b) => ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) || cmp(a, b);

export const PinButton = ({ pinned, onToggle, testid }) => (
  <button
    type="button" data-testid={testid} aria-pressed={!!pinned}
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    title={pinned ? "Unpin" : "Pin to top"} aria-label={pinned ? "Unpin" : "Pin to top"}
    className={`transition-colors shrink-0 ${pinned ? "text-[#CBA96C]" : "text-[#6B7280] hover:text-[#CBA96C]"}`}
  ><Pin className={`w-3 h-3 ${pinned ? "fill-current" : ""}`}/></button>
);

/**
 * `(row) => void` that flips a row's pin, optimistically — the row moves at once and moves back if
 * the server refuses. `kind` is "transaction" or "payment"; `setRows` is the page's list setter.
 */
export function usePinToggle(kind, setRows) {
  return useCallback(async (row) => {
    const next = !row.pinned;
    const apply = (value) => setRows(rs => rs.map(r => (r.id === row.id ? { ...r, pinned: value } : r)));
    apply(next);
    try {
      if (next) await api.put(`/admin/pins/${kind}/${row.id}`);
      else await api.delete(`/admin/pins/${kind}/${row.id}`);
      toast.success(next ? "Pinned to top" : "Unpinned");
    } catch (e) {
      apply(!next);
      toast.error(e?.response?.data?.detail || "Could not update pin");
    }
  }, [kind, setRows]);
}

/**
 * The invoice number on a completed case: click to find the payment, copy to quote it.
 *
 * The link carries an **all-time** window deliberately. Payments is date-filtered now, and a
 * closed case is by definition old — sending the reader's current window would land them on an
 * empty list for a payment that exists, which is indistinguishable from a missing record. It also
 * carries `?q=`, so the ledger opens already filtered to this one bill rather than to the search
 * box the reader then has to type into.
 *
 * The row itself opens the case, so both controls stop the click from propagating.
 */
export const InvoiceCell = ({ t }) => {
  const nav = useNavigate();
  const no = invoiceNo(t);
  if (!no) {
    // Not every completed case was paid for — a case closed unpaid, or refunded, has no order.
    // Said in words, because an empty cell reads as data missing rather than as "there was none".
    return <div className="text-xs text-[#6B7280] italic pt-1.5">No bill</div>;
  }
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(no);
      toast.success("Invoice number copied");
    } catch {
      toast.error("Could not copy — clipboard unavailable");
    }
  };
  return (
    <div className="pt-1.5 min-w-0">
      <button
        type="button" data-testid={`invoice-${t.id}`}
        onClick={(e) => {
          e.stopPropagation();
          nav(`/admin/payments?q=${encodeURIComponent(no)}&from=${ALL_TIME}&to=${isoToday()}`);
        }}
        title={`Find this payment — invoice ${no}`}
        className="font-mono text-xs text-[#A0AABF] hover:text-[#CBA96C] transition-colors truncate block text-left"
      >{no}</button>
      <button
        data-testid={`copy-invoice-${t.id}`} onClick={copy}
        title={`Copy invoice number — ${no}`} aria-label="Copy invoice number"
        className="text-[#6B7280] hover:text-[#CBA96C] transition-colors mt-1"
      ><Copy className="w-3 h-3"/></button>
    </div>
  );
};

/**
 * The customer, with the document underneath and the two ways to reach them beside the name.
 *
 * Used by every pending queue and by All Open, deliberately: admin's next step on a pending case
 * is almost always to contact somebody, so the row should lead with *who* rather than *what*. The
 * document type is still there — one line down, smaller — because it's context for the call, not
 * the thing being triaged.
 *
 * WhatsApp opens a chat; the mail icon copies the address rather than firing a `mailto:` — admin
 * is usually pasting it into their own mail client, and a hijacked `mailto:` handler is a dead end.
 */
export const CustomerCell = ({ t, onOpen }) => {
  const wa = toWhatsAppLink(t.customer_mobile);
  const email = t.customer_email && t.customer_email !== "?" ? t.customer_email : null;
  // The template's title, not its key: `subtype` is an internal identifier and rendering it
  // directly showed customers things like "affidavit · one and same person".
  const doc = t.template_title || `${t.service_type || ""}`.replace(/_/g, " ");
  const copyEmail = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied ${email}`);
    } catch {
      toast.error("Could not copy — clipboard unavailable");
    }
  };
  return (
    <div className="min-w-0 pt-1.5">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-sm text-white truncate cursor-pointer" onClick={onOpen}>
          {t.customer_name || "Unknown customer"}
        </span>
        {wa ? (
          <a href={wa} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
             title={`WhatsApp ${t.customer_mobile}`} aria-label="Message on WhatsApp"
             className="text-[#25D366] hover:opacity-80 shrink-0">
            <MessageCircle className="w-3.5 h-3.5"/>
          </a>
        ) : (
          <span title="No mobile number on file" className="text-[#4B5563] shrink-0">
            <MessageCircle className="w-3.5 h-3.5"/>
          </span>
        )}
        {email && (
          <button onClick={copyEmail} data-testid={`copy-email-${t.id}`}
                  title={`Copy ${email}`} aria-label="Copy customer email"
                  className="text-[#6B7280] hover:text-[#CBA96C] transition-colors shrink-0">
            <Mail className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
      <div className="text-[11px] text-[#6B7280] truncate cursor-pointer" onClick={onOpen}>{doc || "—"}</div>
    </div>
  );
};

/**
 * Who the case is waiting on, derived from `action_items` owners (core.lifecycle
 * .compute_action_items). A case can legitimately be waiting on more than one party at once —
 * e.g. admin owes a reply while the reviewer hasn't started — so this renders every distinct
 * owner rather than picking one.
 */
export const PENDING_AT_ORDER = ["customer", "admin", "reviewer"];
export const PendingAtCell = ({ t }) => {
  const owners = PENDING_AT_ORDER.filter(o => (t.action_items || []).some(i => i.owner === o));
  if (owners.length === 0) {
    return <span className="text-xs text-[#4B5563]">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {owners.map(o => (
        <span key={o} className={`pill pill-xs pill-owner-${o}`} title={
          (t.action_items || []).filter(i => i.owner === o).map(i => i.label).join(", ")
        }>{o === "reviewer" ? "Advocate" : o[0].toUpperCase() + o.slice(1)}</span>
      ))}
    </div>
  );
};

export const ActionBadges = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {items.map(i => (
        <span key={i.reason} className={`pill pill-xs pill-owner-${i.owner}`}>{i.label}</span>
      ))}
    </div>
  );
};

/**
 * How a customer signs in — Google, Facebook, or both once they have used both.
 *
 * **Read from the ids, not from `provider`.** `provider` on the row is whichever one was used
 * *most recently* (`resolve_social_user` rewrites it on every sign-in), while `google_id` /
 * `facebook_id` are what the account is actually **linked** to and are never unset. A customer who
 * arrived through Google in March and signed in with Facebook today is linked to both, and showing
 * only `provider` would hide that — which matters on a support call, because either one gets them
 * in. The most recently used leads, and the tooltip says which is which.
 *
 * Customers hold no password at all, so this is the entire answer to "how does this person get into
 * their account?" — that is why it belongs on the list and not only on the detail page.
 */
const PROVIDER_LABELS = { google: "Google", facebook: "Facebook" };

const linkedProviders = (user) => {
  const linked = Object.keys(PROVIDER_LABELS).filter(p => user?.[`${p}_id`]);
  // A row from before the ids were stored carries `provider` on its own; trust it rather than
  // reporting an account with a working sign-in as having none.
  if (!linked.length && user?.provider) linked.push(user.provider);
  const current = user?.provider;
  return linked.filter(p => p === current).concat(linked.filter(p => p !== current));
};

/** The same answer as a plain string, for table sorting and search. */
export const signInLabel = (user) =>
  linkedProviders(user).map(p => PROVIDER_LABELS[p] || p).join(" ");

export const SignInBadges = ({ user, className = "" }) => {
  const linked = linkedProviders(user);
  if (!linked.length) {
    return (
      <span className={`text-xs text-[#4B5563] ${className}`}
            title="No sign-in provider is linked to this account">—</span>
    );
  }
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {linked.map(p => (
        <span key={p} className={`pill pill-xs pill-${p}`}
              title={p === user?.provider
                ? `Last signed in with ${PROVIDER_LABELS[p] || p}`
                : `Account is also linked to ${PROVIDER_LABELS[p] || p}`}>
          {PROVIDER_LABELS[p] || p}
        </span>
      ))}
    </div>
  );
};

// Assign cell for the "Pending: Admin" tab — self / top-3-by-fewest-open-cases / search, all
// behind one 3-dot menu. Its own component (not inlined in the row renderer) so the search box
// can hold its own local state without every row in the table re-rendering on each keystroke.
export const AssignMenu = ({ t, reviewers, onClaimSelf, onAssign }) => {
  const [q, setQ] = useState("");
  const top3 = [...reviewers].sort((a, b) => (a.open_count ?? 0) - (b.open_count ?? 0)).slice(0, 3);
  const query = q.trim().toLowerCase();
  const filtered = query ? reviewers.filter(r => r.name.toLowerCase().includes(query)) : [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`assign-menu-${t.id}`} aria-label="Assign reviewer" className="p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors shrink-0">
          <MoreVertical className="w-3.5 h-3.5"/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white w-60" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuItem data-testid={`assign-self-${t.id}`} onClick={() => onClaimSelf(t.id)} className="cursor-pointer focus:bg-white/5 focus:text-white">
          Handle myself
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10"/>
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6B7280]">Top reviewers (fewest open)</DropdownMenuLabel>
        {top3.map(r => (
          <DropdownMenuItem key={r.id} data-testid={`assign-top-${t.id}-${r.id}`} onClick={() => onAssign(t.id, r.id)} className="cursor-pointer focus:bg-white/5 focus:text-white flex items-center justify-between gap-2">
            <span className="truncate">{r.name}</span>
            <span className="text-[10px] text-[#6B7280] shrink-0">{r.open_count ?? 0} open</span>
          </DropdownMenuItem>
        ))}
        {reviewers.length === 0 && <div className="px-2 py-1.5 text-xs text-[#6B7280]">No reviewers yet.</div>}
        <DropdownMenuSeparator className="bg-white/10"/>
        <div className="px-2 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <Input data-testid={`assign-search-${t.id}`} placeholder="Search reviewers…" value={q} onChange={(e) => setQ(e.target.value)} className="h-7 text-xs bg-transparent border-white/15 text-white"/>
        </div>
        {query && filtered.map(r => (
          <DropdownMenuItem key={r.id} onClick={() => onAssign(t.id, r.id)} className="cursor-pointer focus:bg-white/5 focus:text-white flex items-center justify-between gap-2">
            <span className="truncate">{r.name}</span>
            <span className="text-[10px] text-[#6B7280] shrink-0">{r.open_count ?? 0} open</span>
          </DropdownMenuItem>
        ))}
        {query && filtered.length === 0 && <div className="px-2 py-1.5 text-xs text-[#6B7280]">No match.</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * The dark, searchable, sortable table shell used by every admin list page.
 *
 * The list pages were built on the light shadcn `Table` inside a white `Card`, while the
 * transaction queues use this dark grid — so moving between admin pages meant switching visual
 * language mid-task. This is that grid, extracted, so the pages differ only in their columns.
 *
 * Sorting and searching live here rather than in each page: they were absent from every page but
 * Transactions, and re-implementing them six times is how they drift.
 *
 * `renderRow(row)` supplies the cells only — the grid wrapper, keying and hover are handled here.
 */
export const AdminTable = ({
  columns, gridCols, rows, renderRow, rowKey = (r) => r.id,
  matches, accessors = {}, searchPlaceholder = "Search…",
  empty = "Nothing here yet.", defaultSort, testid = "admin-table", toolbar, pinnable = false,
}) => {
  // Keyed by the table's testid so each list remembers its own query — see lib/stickySearch.js.
  const [search, setSearch, clearSearch] = useStickySearch(testid);
  // Seeded from `?q=` on mount, so one list can link into another already filtered — the Completed
  // queue's invoice number opens Payments on that one bill. Read once and never written back: the
  // query lives in sessionStorage, and putting customer names into a shareable URL is the thing
  // stickySearch.js deliberately avoids.
  const [urlParams] = useSearchParams();
  useEffect(() => {
    const seed = urlParams.get("q");
    if (seed) setSearch(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sortField, setSortField] = useState(defaultSort || columns[0]?.key);
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortField === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortField(key); setSortDir("asc"); }
  };
  const q = search.trim().toLowerCase();
  const filtered = (rows || []).filter(r => !q || (matches ? matches(r, q) : true));
  const accessor = accessors[sortField];
  const byColumn = accessor
    ? (a, b) => {
        const av = accessor(a), bv = accessor(b);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
    : () => 0;
  // `pinnable` tables hold pinned rows above the rest, whatever the sort (see pinnedFirst).
  const visible = (accessor || pinnable)
    ? [...filtered].sort(pinnable ? pinnedFirst(byColumn) : byColumn)
    : filtered;

  // Paginate the *display*. `fetchAll` has already loaded every row so that search and sort work
  // across the whole list rather than the visible slice; this bounds what the DOM has to render,
  // which is what keeps these pages responsive as transactions accumulate.
  const pageSize = usePageSize();
  const paged = usePaged(visible, pageSize);

  // Any change to the query resets to the first page. Searching while on page 4 and being shown
  // "no results" — because the three matches are on page 1 — is a bug people report as the search
  // being broken.
  useEffect(() => { paged.setPage(0); }, [q, sortField, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        {toolbar}
        <div className="relative w-full sm:w-72">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            data-testid={`${testid}-search`} value={search} onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent border border-white/15 rounded-full pl-9 pr-9 py-2 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#CBA96C]/50"
          />
          {/* The only thing that forgets the query. Navigation never does — see stickySearch.js. */}
          {search && (
            <button type="button" onClick={clearSearch} data-testid={`${testid}-search-clear`}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/10 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="bg-[#111623] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-x-auto" data-testid={testid}>
        <Pagination {...paged} testid={`${testid}-pager`} />
        <div className={`hidden md:grid ${gridCols} gap-2 px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase text-[#6B7280] min-w-max`}>
          {columns.map(col => (
            <button key={col.key} data-testid={`${testid}-sort-${col.key}`} onClick={() => toggleSort(col.key)}
                    className="flex items-center gap-1 hover:text-white transition-colors text-left">
              {col.label}
              {sortField === col.key
                ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>)
                : <ArrowUpDown className="w-3 h-3 opacity-40"/>}
            </button>
          ))}
        </div>
        {visible.length === 0 && (
          <div className="text-center text-[#6B7280] py-10 text-sm">
            {q ? `Nothing matches "${search}".` : empty}
          </div>
        )}
        {paged.visible.map(r => (
          <div key={rowKey(r)} className={`grid ${gridCols} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors min-w-max`}>
            {renderRow(r)}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Monospace id with a copy button — used for txn/receipt/order ids across the admin lists. */
export const CopyableId = ({ value, label = "ID", testid, onOpen }) => {
  if (!value) return <span className="text-xs text-[#4B5563]">—</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  return (
    <div className="flex items-center gap-1.5 pt-1.5 min-w-0">
      <span className={`font-mono text-xs text-[#A0AABF] truncate ${onOpen ? "hover:text-white cursor-pointer" : ""}`} onClick={onOpen}>
        {String(value).slice(0, 8)}
      </span>
      <button data-testid={testid} onClick={copy} title={`Copy full ${label} — ${value}`} aria-label={`Copy ${label}`}
              className="text-[#6B7280] hover:text-[#CBA96C] transition-colors shrink-0">
        <Copy className="w-3 h-3"/>
      </button>
    </div>
  );
};

/** Date over time on two lines — matches the customer dashboard's treatment. */
export const DateTimeCell = ({ value, onOpen }) => {
  if (!value) return <span className="text-xs text-[#4B5563]">—</span>;
  const d = new Date(value);
  return (
    <div className={`pt-1.5 ${onOpen ? "cursor-pointer" : ""}`} onClick={onOpen}>
      <div className="text-xs text-[#A0AABF]">{fmtDate(d)}</div>
      <div className="text-[10px] text-[#6B7280]">{d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
    </div>
  );
};

/**
 * Row styling for a case this admin has not read.
 *
 * "Unread" is computed by the server (`_attach_unread` in routers/admin.py) and means *never
 * opened, or changed since I last opened it* — so a case read last week goes gold again the moment
 * the advocate submits findings. It is per-admin: two people working the same queue each have
 * their own.
 *
 * The read state returns `border-transparent` rather than no border at all, so marking a row read
 * does not shift its contents 2px to the left. A queue where rows twitch as you work down it is
 * worse than one with no marker.
 */
export function unreadRowClass(t) {
  return t?.unread
    ? "border-l-[3px] border-[#CBA96C] bg-[#CBA96C]/[0.09] shadow-[inset_0_0_0_1px_rgba(203,169,108,0.10)]"
    : "border-l-[3px] border-transparent";
}

/**
 * A message-count icon's colour: grey when there's nothing to see (no messages from that party at
 * all) or nothing *new* to see (the case has been opened since it last changed — the same
 * `t.unread` this row's own left-border reads, see `unreadRowClass` above). Colour is reserved for
 * "there's a message here you have not looked at yet", not just "a message exists" — a customer's
 * three-message-old thread should not sit there blue forever once admin has actually read it.
 */
export function msgIconClass(count, t, activeColor) {
  return count > 0 && t?.unread ? activeColor : "text-[#6B7280]";
}
